import { cloneInstanceClass } from '~/helpers/cloneInstanceClass'
import { MediaAsset } from '~/models/Asset'

/**
 * XTL time attributes are declared with `timeFormat="100ns_units"` and are emitted
 * verbatim, without any rounding. Asset times, on the other hand, are floats in the
 * editor: `TimelineItem::endTime` truncates to 4 decimals, and trim/move/IO-point
 * helpers stack raw float arithmetic on top of that, so the error compounds down a
 * track.
 *
 * The renderer instead lays clips end to end and derives every position from
 * `clip.stop - clip.start`, while effects carry absolute times. Both were rounded
 * separately over the same data, so they landed on different ticks - e.g. a volume
 * effect starting at `397637646` while its clip started at `397637647`.
 *
 * Rounding once, on the way into XTL generation, makes the document integer
 * consistent. `endTime` and `mediaEnd` are derived getters, so as long as they are
 * never rounded independently we get exact identity:
 *
 *   mediaEnd - mediaStart === duration === endTime - startTime
 *
 * which is what makes clip boundaries and effect boundaries land on the same tick.
 *
 * What matters is *which* values get rounded. Round the ordered endpoints
 * (`startTime`, `endTime`) and derive `duration` from them - never round the
 * additive parts (`startTime`, `duration`) independently:
 *
 *   - Endpoints are already non-decreasing (`next.startTime >= asset.endTime`), and
 *     `Math.floor` is monotone, so rounding them keeps that ordering. An adjacent
 *     pair flush in the editor stays exactly flush (`floor(e) - floor(e) === 0`), so
 *     no gap is invented, and no gap can go negative.
 *
 *   - Rounding the parts instead loses a tick whenever
 *     `frac(startTime) + frac(duration) >= 1`, because `floor(x) + floor(y)` can be
 *     less than `floor(x + y)`. That silently shortens `endTime` and opens a 1-tick
 *     blank clip against the next asset on every such boundary.
 *
 * `Math.round` is not usable either: it is not superadditive, so
 * `round(startTime) + round(duration)` can exceed `round(endTime)`. The resulting
 * negative gap is dropped by `XTL.blankClip` (`duration <= 0`), after which the
 * end-to-end layout stops tracking `startTime` and every later clip desynchronises.
 *
 * @param {number} value timeline time in 100ns units
 * @returns {number} integer timeline time in 100ns units
 */
function fixedPoint(value) {
  return Math.floor(value)
}

/**
 * `mediaFileDuration` is getter-only on MediaAsset, and VideoAsset's setter would
 * additionally reset `originDuration` and `duration` (undoing trims), so both private
 * fields are rounded in place. `_maxDuration` is not always `_mediaFileDuration`
 * (ImageAsset/TextAsset use Infinity, VideoAsset has its own setter) and may be
 * Infinity, which `Math.round` passes through untouched.
 *
 * This must run before `duration`, whose setter clamps against `_maxDuration` and
 * would otherwise store a fractional value back.
 *
 * @param {Assets.MediaAsset} fixed
 */
function fixMediaBounds(fixed) {
  /* eslint-disable no-underscore-dangle */
  /* eslint-disable no-param-reassign */
  fixed._mediaFileDuration = fixedPoint(fixed._mediaFileDuration)
  fixed._maxDuration = fixedPoint(fixed._maxDuration)
  /* eslint-enable no-underscore-dangle */
}

/**
 * @param {Assets.Asset} asset
 * @returns {Assets.Asset} a shallow clone with integer times, `_id` preserved
 */
export function toFixedPointAsset(asset) {
  const fixed = cloneInstanceClass(asset)

  if (fixed instanceof MediaAsset) {
    fixMediaBounds(fixed)
  }

  // `endTime` is a derived getter (`startTime + duration`), so it has to be read
  // before `startTime` is overwritten.
  const { endTime } = fixed

  // setters clamp into the valid range: [MIN_TIMELINE_ITEM_DURATION, _maxDuration]
  // and >= 0 respectively
  fixed.startTime = fixedPoint(fixed.startTime)
  // Assigning `endTime` sets `duration = endTime - startTime`, so the length is
  // derived from the two rounded endpoints rather than rounded on its own.
  fixed.endTime = fixedPoint(endTime)

  if (fixed instanceof MediaAsset) {
    // `mediaEnd` is derived (`mediaStart + duration`), so it must not be rounded on
    // its own - that is exactly what would let the emitted clip length drift away
    // from `duration` and desynchronise the layout from the effect windows.
    fixed.mediaStart = fixedPoint(fixed.mediaStart)
  }

  // Only AudioAsset/VideoAsset declare fades; TransitionAsset has no such fields.
  if ('fadeInDuration' in fixed) {
    fixed.fadeInDuration = fixedPoint(fixed.fadeInDuration)
  }
  if ('fadeOutDuration' in fixed) {
    fixed.fadeOutDuration = fixedPoint(fixed.fadeOutDuration)
  }

  return fixed
}

/**
 * @param {Assets.Asset[]} assets
 * @returns {Assets.Asset[]}
 */
export function toFixedPointAssets(assets) {
  return assets.map(toFixedPointAsset)
}
